/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { components } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import styles from "./index.module.scss";
import { Search } from "../Icons/general-interface-2";
import { Text } from "../Type";
import ProfileIcon from "../UserInterface/ProfileIcon";
import { clearIndicator, dropdownIndicator, indicatorSeparator, placeholder, valueContainer } from "../FilterDropdowns/commonStyles";
import { UserRoleTypeType, UserType } from "../../types/userType";

const GET_USERS = gql`
  query Users(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    users(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      first_name
      last_name
      email
      role {
        type
        name
      }
    }
    usersConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

type UserTypeOption = {
  data: UserType;
  label: string;
  value: string;
};

type PropsType = {
  excludedUserIds?: string[];
  roleTypes?: UserRoleTypeType[];
  onChange: (value: UserType[]) => void;
  cepOnly?: boolean;
};

const CareTeamSelect = ({
  excludedUserIds = [],
  roleTypes,
  onChange,
  cepOnly = false,
}: PropsType) => {
  const pageSize = 10;
  const [pageIndex, setPageIndex] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [value, setValue] = useState<UserTypeOption[]>([]);
  const [, setBoundaryError] = useState<Error | null>(null);
  const includedRoleTypes = roleTypes?.length ? roleTypes : ["authorizer", "sales_rep", "motion_support"];

  const where = (search = "") => {
    const and: { [key: string]: boolean | string | string[] }[] = [
      { "role.type_in": includedRoleTypes },
    ];
    if (cepOnly) {
      and.push({ cep: true });
    }
    return {
      id_nin: excludedUserIds,
      _or:
        search && search.length > 0
          ? [
              { first_name_contains: search },
              { last_name_contains: search },
              { email_contains: search },
            ]
          : [],
      _and: and,
    };
  };

  const { data, refetch } = useQuery(GET_USERS, {
    variables: {
      limit: pageSize,
      sort: "first_name:ASC",
      start: pageIndex * pageSize,
      where: where(),
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const fetchData = async ({ pageIndex, search }) => {
    const variables: {
      limit: number;
      start: number;
      sort?: string;
      where?: any;
      connectionWhere?: any;
    } = {
      limit: pageSize,
      start: pageIndex * pageSize,
    };

    variables.where = where(search);
    variables.connectionWhere = where;
    variables.sort = "first_name:ASC";

    const response = await refetch(variables);
    return response;
  };

  const MultiValueLabel = (props) => {
    return (
      <components.MultiValueLabel {...props}>
        {`${props.data.data.first_name} ${props.data.data.last_name}`}
      </components.MultiValueLabel>
    );
  };

  const Option = (props) => (
    <components.Option {...props}>
      <div className={styles.optionLabel}>
        <ProfileIcon
          name={`${props.data.data.first_name} ${props.data.data.last_name}`}
          userRole={`${props.data.data.role.name}`}
          size="small"
        />
        <Text className={styles.optionValue}>
          {props.data.data.first_name} {props.data.data.last_name}
        </Text>
        <Text size="small" fade>
          ({props.data.data.email})
        </Text>
      </div>
    </components.Option>
  );

  const Placeholder = (props) => {
    return (
      <components.Placeholder {...props}>
        <div className={styles.placeholder}>
          <Search className={styles.placeholderIcon} />
          {props.children}
        </div>
      </components.Placeholder>
    );
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      display: "inline-flex",
      background: "white",
      width: "100%",
      maxHeight: "150px",
      padding: "0 10px",
      border: state.isFocused
        ? `1px solid ${styles.grey_400}`
        : `1px solid ${styles.grey_200}`,
      boxShadow: state.isFocused
        ? `0px 0px 0px 3px ${styles.blue_200}`
        : `0px 0px 0px 3px transparent`,
      borderRadius: "4px",
      "&:focus, &:hover": {
        borderColor: styles.grey_400,
      },
    }),
    clearIndicator,
    dropdownIndicator,
    indicatorSeparator,
    menu: (provided) => ({
      ...provided,
      maxHeight: "150px",
      borderRadius: "4px",
      boxShadow: "0px 1px 3px 0px #3F3F4426, 0px 0px 0px 1px #3F3F440D",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
      maxHeight: "150px",
    }),
    placeholder,
    valueContainer,
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: styles.grey_100,
      borderRadius: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      transition: "color 150ms ease-in-out",
      ":hover": {
        backgroundColor: styles.grey_100,
        color: styles.red_300,
      },
    }),
  };

  const loadOptions = async (search, loadedOptions, { page }) => {
    setPageIndex(page);
    let filteredOptions = data?.users;
    if (search) {
      const response = await fetchData({ pageIndex: page - 1, search });
      filteredOptions = response.data.users;
    }
    return {
      options: filteredOptions.map((u) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
        data: u,
      })),
      hasMore: filteredOptions.length > 0 && loadedOptions.length % pageSize === 0,
      additional: { page: page + 1, },
    };
  };

  return (
    <AsyncPaginate
      inputValue={keyword}
      onInputChange={setKeyword}
      value={value}
      debounceTimeout={300}
      loadOptions={loadOptions}
      styles={customStyles}
      isSearchable
      maxMenuHeight="197px"
      placeholder="Search by name or email"
      isMulti
      onChange={(value) => {
        setValue(value);
        onChange(value.map((v) => v.data));
      }}
      additional={{ page: 1 }}
      components={{
        MultiValueLabel,
        Option,
        Placeholder,
      }}
    />
  );
};
export default CareTeamSelect;
